import {
  IssueCase,
  IssueCaseCategory,
  IssueCaseCreatorType,
  IssueCaseStatusCode,
  IssueCaseSubcategory,
  IssueProduct,
  IssueSourceType,
  IssueTaskCategory,
  IssueTaskCreatorType,
  IssueTaskStatus,
  OrderDoc,
  OrderProduct,
  ProductSoldOutTemplate,
  ProductStateCode,
  UserDoc,
  issueCaseSubcategoryMap,
} from '@gooduncles/gu-app-schema';
import { message, notification } from 'antd';
import { senderKey } from 'src/constants';
import { v4 } from 'uuid';

import { errorObjectToString, formatNumber } from 'src/utils/common';
import { formatDate, getKakaoTalkRequestOptions } from 'src/utils/date-util';
import {
  callSendKakaoAlimTalkForChangeVolume,
  callSendKakaoAlimTalkForProductSoldout,
} from 'src/utils/firebase/firebase-callable';
import {
  createIssueCase,
  createIssueTask,
  getAlimtalkMessage,
  getIssueCase,
  getUser,
  updateOrder,
} from 'src/utils/firebase/firebase-short-cut';
import { getIssueTaskInitialData } from 'src/utils/issue-task-util';
import { getIssueCaseInitialData, parseOrderProductsToIssueProduct } from 'src/utils/issue/issue-util';
import { AuditLogger } from 'src/utils/audit-logger';
import { ConsoleLogger } from 'src/utils/logger';
import { organizeKakaotalkResultMessage } from 'src/utils/nhn-notification-util';
import { calcProductUnitVolume, calculateOrderAmounts } from 'src/utils/order-util';

const logger = ConsoleLogger.getInstance();
const auditLogger = AuditLogger.getInstance();
const logName = '주문 관리';

/**
 * 카카오 알림톡 발송을 위한 사전 검증 및 사용자 정보 조회
 * 로딩 메시지 표시, 사용자 전화번호 및 senderKey 유효성 검증을 수행합니다.
 */
async function prepareKakaoAlimTalk(userId: string): Promise<{
  senderKey: string;
  userTel: string;
  storeId: string;
  disableNotificationAtDawn: boolean;
  uiMessageKey: string;
}> {
  const uiMessageKey = v4();
  message.open({
    type: 'loading',
    content: '카톡 메시지 발송 요청을 진행합니다...',
    duration: 0,
    key: uiMessageKey,
  });

  const user = await getUser(userId);
  if (!user?.userTel) {
    message.destroy(uiMessageKey);
    throw new Error('유효한 전화번호를 찾지 못했습니다.');
  }

  if (!senderKey) {
    message.destroy(uiMessageKey);
    throw new Error('카카오 알림톡 발송을 위한 senderKey가 없습니다.');
  }

  return {
    senderKey,
    userTel: user.userTel,
    storeId: user.storeId,
    disableNotificationAtDawn: user.disableNotificationAtDawn ?? false,
    uiMessageKey,
  };
}

/**
 * 카카오 알림톡 발송 결과를 처리하고 알림을 표시합니다.
 */
async function handleKakaoAlimTalkResult(
  result: { data: { result: string; reason?: string; alimtalkMessageDocId?: string | null } },
  uiMessageKey: string,
  requestDate: string | undefined,
): Promise<void> {
  message.destroy(uiMessageKey);

  if (result.data.result !== 'success') {
    throw new Error(result.data.reason);
  }

  if (!result.data.alimtalkMessageDocId) {
    throw new Error('요청은 성공했으나, ID를 받지 못했습니다.');
  }

  const alimtalkMessageDoc = await getAlimtalkMessage<ProductSoldOutTemplate>(result.data.alimtalkMessageDocId);
  if (!alimtalkMessageDoc) {
    throw new Error('요청은 성공했으나, 메시지 정보를 받지 못했습니다.');
  }

  const description = organizeKakaotalkResultMessage(alimtalkMessageDoc.recipientWithResultList);

  notification.success({
    message: `카톡 메시지 ${requestDate ? '⏰ 예약' : '즉시'}발송 요청 완료`,
    description,
    className: 'pre-line-notification',
  });
}

/**
 * 카테고리에 해당하는 기본 서브카테고리를 반환합니다.
 */
const getDefaultSubcategory = (category: IssueCaseCategory): IssueCaseSubcategory | null => {
  const subcategoryMap = issueCaseSubcategoryMap[category as keyof typeof issueCaseSubcategoryMap];
  if (!subcategoryMap) return null;
  const keys = Object.keys(subcategoryMap);
  return keys.length > 0 ? (keys[0] as IssueCaseSubcategory) : null;
};

export interface CreateOrderProductIssueParams {
  order: OrderDoc;
  store: { storeId: string; storeName: string };
  product: OrderProduct;
  user: UserDoc;
  category: IssueCaseCategory;
  subcategory?: IssueCaseSubcategory | null;
  memo: string;
  changeType: 'volume_change' | 'sold_out';
  newVolume?: number;
  /** 처리 경로 (예: '주문 관리/주문별 보기 - 수량 변경') */
  source?: string;
}

/**
 * 주문 상품 변경 시 issueCase + issueTask(INTERNAL) 생성
 */
export const createOrderProductIssue = async (
  params: CreateOrderProductIssueParams,
): Promise<{ issueCaseId: string; issueTaskId: string }> => {
  const { order, store, product, user, category, subcategory, memo, changeType, newVolume, source } = params;

  // 1. IssueCase 초기 데이터 생성
  const issueCaseBase: IssueCase = getIssueCaseInitialData({
    sourceType: IssueSourceType.ORDER,
    sourceId: order._id,
    sourceReferenceDate: order.deliveryDate ?? order.date,
    storeId: store.storeId,
    storeName: store.storeName,
    creatorId: user._id,
    creatorType: user.role as IssueCaseCreatorType,
    category,
    subcategory: subcategory ?? getDefaultSubcategory(category),
  });

  // 2. 상품 정보 및 메모 추가
  // 수량 변경: 변경 수량(차이)만 등록 (예: 2 → 3이면 +1, 2 → 2.33이면 +0.33)
  // 품절 처리: 기존 수량의 마이너스 (예: 2개 품절 → -2)
  // 부동소수점 정밀도 문제 방지를 위해 소수점 3자리로 반올림
  const rawVolumeDiff = changeType === 'sold_out' ? -product.volume : (newVolume ?? product.volume) - product.volume;
  const volumeDiff = Math.round(rawVolumeDiff * 1000) / 1000;

  const issueProduct: IssueProduct = parseOrderProductsToIssueProduct({
    ...product,
    volume: volumeDiff,
  });

  const issueCaseData: IssueCase = {
    ...issueCaseBase,
    status: IssueCaseStatusCode.IN_PROGRESS,
    privateNote: memo || null,
    products: [issueProduct],
    amount: issueProduct.amount,
  };

  // 3. IssueCase 생성
  const issueCaseId = await createIssueCase(issueCaseData);

  // 4. 생성된 IssueCase 조회 (issueTask 생성용)
  const issueCaseDoc = await getIssueCase(issueCaseId);
  if (!issueCaseDoc) {
    throw new Error('IssueCase 생성 후 조회에 실패했습니다.');
  }

  // 5. IssueTask(INTERNAL) 초기 데이터 생성 - 완료 상태로 생성
  const issueTaskBase = getIssueTaskInitialData(issueCaseDoc, {
    creatorId: user._id,
    creatorType: user.role as IssueTaskCreatorType,
    category: IssueTaskCategory.INTERNAL,
  });

  // 처리 경로 생성 (예: '주문 관리/주문별 보기 - 수량 변경')
  const changeTypeLabel = changeType === 'sold_out' ? '품절 처리' : '수량 변경';
  const sourceNote = source ? `${source} - ${changeTypeLabel}` : changeTypeLabel;

  const issueTaskData = {
    ...issueTaskBase,
    status: IssueTaskStatus.COMPLETED,
    targetDate: order.date,
    finishedAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
    privateNote: sourceNote,
  };

  // 6. IssueTask 생성
  const issueTaskId = await createIssueTask(issueTaskData);

  return { issueCaseId, issueTaskId };
};

export interface UpdateOrderProductVolumeParams {
  order: OrderDoc;
  product: OrderProduct;
  newVolume: number;
  sendKakaoAlimTalk?: boolean;
}

/**
 * 주문 상품 수량 변경 로직 (기존 onCellValueChangedWithUpdateForOrderProduct 로직)
 */
export const updateOrderProductVolume = async (params: UpdateOrderProductVolumeParams): Promise<void> => {
  const { order, product, newVolume, sendKakaoAlimTalk = true } = params;

  // 1. 변경된 수량을 주문 내용에 반영
  const newOrderProducts = order.products.map((orderProduct) => {
    if (orderProduct.productId === product.productId) {
      return {
        ...orderProduct,
        state: ProductStateCode.STOCK,
        volume: newVolume,
      };
    }
    return orderProduct;
  });

  const { totalAmount, grandTotal, paidAmount } = calculateOrderAmounts(newOrderProducts, order.deliveryFee ?? 0);

  // 2. 주문 업데이트
  await updateOrder(
    order._id,
    {
      products: newOrderProducts,
      totalAmount,
      paidAmount,
      grandTotal,
    },
    { audit: { suppress: true } },
  );

  const newUnitVolume = calcProductUnitVolume(product.measure, newVolume);

  // 3. 변경 내용 기록
  logger.logOrder('주문 상품 수량 변경', {
    orderId: order._id,
    storeId: order.storeId,
    before: order,
    after: {
      ...order,
      products: newOrderProducts,
      totalAmount,
      paidAmount,
      grandTotal,
    },
    reason: '실거래가 반영',
    reasonForUser: `고객님이 구매하신 ${product.fullName}의 출고량은 ${newUnitVolume}이며 ${formatNumber(
      product.price * newVolume,
    )}원으로 적용됩니다. 이용해주셔서 감사합니다.`,
    alimtalkTemplateCode: sendKakaoAlimTalk ? 'volume-change' : null,
  });
  auditLogger.log({
    action: 'UPDATE',
    entity: 'order',
    entityId: order._id,
    summary: `주문 상품 수량 변경 - ${product.fullName} (${product.volume} → ${newVolume})`,
    changes: [
      { field: 'volume', before: product.volume, after: newVolume },
      { field: 'grandTotal', before: order.grandTotal, after: grandTotal },
    ],
    metadata: { storeId: order.storeId, productId: product.productId, reason: '실거래가 반영' },
  });

  notification.success({
    message: '주문 상품 수정 완료',
    description: `총 금액: ${order.grandTotal} -> ${grandTotal}`,
  });

  if (!sendKakaoAlimTalk) {
    return;
  }

  // 4. 카톡 메시지 발송
  const kakaoInfo = await prepareKakaoAlimTalk(order.userId);
  const requestDate = kakaoInfo.disableNotificationAtDawn ? getKakaoTalkRequestOptions() : undefined;

  const result = await callSendKakaoAlimTalkForChangeVolume({
    senderKey: kakaoInfo.senderKey,
    templateCode: 'volume-change',
    recipientList: [
      {
        recipientNo: kakaoInfo.userTel,
        templateParameter: {
          date: formatDate(new Date(order.orderDate), 'L월d일'),
          product: product.fullName,
          volume: newUnitVolume,
          price: formatNumber(product.price * newVolume),
        },
        recipientGroupingKey: kakaoInfo.storeId,
      },
    ],
    ...(requestDate ? { requestDate } : {}),
  });

  await handleKakaoAlimTalkResult(result, kakaoInfo.uiMessageKey, requestDate);
};

export interface UpdateOrderProductSoldOutParams {
  order: OrderDoc;
  product: OrderProduct;
  sendKakaoAlimTalk?: boolean;
  /** 시간외 주문건 취소 알림 템플릿 사용 여부 */
  useTimeoutTemplate?: boolean;
}

/**
 * 주문 상품 품절 처리 로직 (기존 SoldOutCellRenderer 로직)
 */
export const updateOrderProductSoldOut = async (params: UpdateOrderProductSoldOutParams): Promise<void> => {
  const { order, product, sendKakaoAlimTalk = true, useTimeoutTemplate = false } = params;

  // 1. 변경된 품목에 따라 주문 내용 수정
  const newOrderProducts = order.products.map((orderProduct) => {
    if (orderProduct.productId === product.productId) {
      return {
        ...orderProduct,
        volume: 0,
        state: ProductStateCode.OUTOFSTOCK,
      };
    }
    return orderProduct;
  });

  const { totalAmount, grandTotal, paidAmount } = calculateOrderAmounts(newOrderProducts, order.deliveryFee ?? 0);

  // 2. 주문 업데이트
  await updateOrder(
    order._id,
    {
      products: newOrderProducts,
      totalAmount,
      paidAmount,
      grandTotal,
    },
    { audit: { suppress: true } },
  );

  // 3. 변경 내용 기록
  logger.logOrder('주문 상품 품절 처리', {
    orderId: order._id,
    storeId: order.storeId,
    before: order,
    after: {
      ...order,
      products: newOrderProducts,
      totalAmount,
      paidAmount,
      grandTotal,
    },
    reason: '품절 처리',
    reasonForUser: `구매하신 ${product.fullName}의 재고가 없어 품절 처리되었습니다. 양해 부탁드립니다.`,
    alimtalkTemplateCode: sendKakaoAlimTalk ? (useTimeoutTemplate ? 'time-out-product' : 'sold-out-product') : null,
  });
  auditLogger.log({
    action: 'UPDATE',
    entity: 'order',
    entityId: order._id,
    summary: `주문 상품 품절 처리 - ${product.fullName}`,
    changes: [
      { field: 'volume', before: product.volume, after: 0 },
      { field: 'state', before: product.state, after: 'OUTOFSTOCK' },
      { field: 'grandTotal', before: order.grandTotal, after: grandTotal },
    ],
    metadata: { storeId: order.storeId, productId: product.productId, reason: '품절 처리' },
  });

  notification.success({
    message: '주문 상품 품절 처리 완료',
    description: `총 금액: ${order.grandTotal} -> ${grandTotal}`,
  });

  if (!sendKakaoAlimTalk) {
    return;
  }

  // 4. 카톡 메시지 발송
  const kakaoInfo = await prepareKakaoAlimTalk(order.userId);
  const requestDate = kakaoInfo.disableNotificationAtDawn ? getKakaoTalkRequestOptions() : undefined;

  const result = await callSendKakaoAlimTalkForProductSoldout({
    senderKey: kakaoInfo.senderKey,
    templateCode: useTimeoutTemplate ? 'time-out-product' : 'sold-out-product',
    recipientList: [
      {
        recipientNo: kakaoInfo.userTel,
        templateParameter: {
          date: formatDate(new Date(order.orderDate), 'L월d일'),
          product: product.fullName,
        },
        recipientGroupingKey: kakaoInfo.storeId,
      },
    ],
    ...(requestDate ? { requestDate } : {}),
  });

  await handleKakaoAlimTalkResult(result, kakaoInfo.uiMessageKey, requestDate);
};

export interface ProcessOrderProductChangeParams extends CreateOrderProductIssueParams {
  sendKakaoAlimTalk: boolean;
  /** 시간외 주문건 취소 알림 템플릿 사용 여부 */
  useTimeoutTemplate?: boolean;
}

/**
 * 주문 상품 변경 처리 (IssueCase 생성 + 주문 업데이트 + 카톡 발송)
 */
export const processOrderProductChange = async (params: ProcessOrderProductChangeParams): Promise<void> => {
  const {
    order,
    store,
    product,
    user,
    category,
    subcategory,
    memo,
    changeType,
    newVolume,
    sendKakaoAlimTalk,
    useTimeoutTemplate,
    source,
  } = params;

  try {
    // 1. IssueCase + IssueTask 생성
    await createOrderProductIssue({
      order,
      store,
      product,
      user,
      category,
      subcategory,
      memo,
      changeType,
      newVolume,
      source,
    });

    // 2. 주문 업데이트 (수량 변경 또는 품절 처리)
    if (changeType === 'volume_change' && newVolume !== undefined) {
      await updateOrderProductVolume({
        order,
        product,
        newVolume,
        sendKakaoAlimTalk,
      });
    } else if (changeType === 'sold_out') {
      await updateOrderProductSoldOut({
        order,
        product,
        sendKakaoAlimTalk,
        useTimeoutTemplate,
      });
    }
  } catch (error) {
    console.error(error);
    const errorMessage = errorObjectToString(error);
    message.error(errorMessage ?? '알 수 없는 에러 발생!');
    auditLogger.log({
      action: 'UPDATE',
      entity: 'order',
      entityId: order._id,
      summary: `${logName} - ${errorMessage}`,
      level: 'error',
    });
    throw error;
  }
};
